/* <video-modal> — the player inside an overlay modal.

   Its whole job is that the video is not live until the modal is open. A YouTube
   iframe present in the DOM loads the player, its scripts and its cookies on page
   load, on every page carrying the section — and keeps playing after the modal is
   dismissed, audible with nothing on screen to stop it. So the src is set on open
   and replaced with about:blank on close (an empty src re-requests the page URL in
   some browsers, and removing the attribute leaves the previous document alive).

   A Shopify-hosted <video> is paused and rewound for the same reason, and started
   — muted — when the modal opens. Muted is not a style choice: every browser
   blocks a script-initiated play() with sound, and a rejected play() leaves the
   shopper looking at a poster frame wondering why the button did nothing. The
   controls are there for them to unmute.

   It reads the ancestor overlay-drawer's `data-open-state` rather than listening
   for clicks, so every close path — the overlay, the button, Escape — is covered
   by one observer. */
class VideoModal extends HTMLElement {
  connectedCallback() {
    this.host = this.closest('overlay-drawer');
    if (!this.host) return;
    this.frame = this.querySelector('[data-video-frame]');
    this.native = this.querySelector('video');
    this.obs = new MutationObserver(() => this.sync());
    this.obs.observe(this.host, { attributes: true, attributeFilter: ['data-open-state'] });
    this.sync();
  }

  disconnectedCallback() {
    if (this.obs) this.obs.disconnect();
  }

  sync() {
    const open = this.host.getAttribute('data-open-state') === 'true';
    if (this.frame) {
      const want = open ? (this.frame.dataset.videoSrc || 'about:blank') : 'about:blank';
      if (this.frame.getAttribute('src') !== want) this.frame.setAttribute('src', want);
    }
    if (!this.native) return;
    if (open) {
      // muted before play(), or the autoplay policy rejects the promise. The
      // rejection is caught because a browser is always free to refuse, and an
      // unhandled rejection in the console helps nobody — the controls remain.
      this.native.muted = true;
      const started = this.native.play();
      if (started && typeof started.catch === 'function') started.catch(() => { /* shopper presses play */ });
    } else {
      try { this.native.pause(); this.native.currentTime = 0; } catch (_) { /* no seekable range yet */ }
    }
  }
}

if (!customElements.get('video-modal')) customElements.define('video-modal', VideoModal);
